import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Button, Checkbox, InputAdornment, Menu, MenuItem } from '@mui/material';
import { Box } from '@mui/system';
import React from 'react';
import { Palette } from 'Palette';
import { SEARCH_TYPE_FILTER_OPTIONS, SEARCH_TYPE_LABEL_MAP } from './headerCenterContentUtils';

// Fixed so the button's width - and therefore the position of its dropdown
// arrow, the divider after it, and the search input's start position - stays
// constant regardless of which option is selected (was auto-sized to the
// label text, so everything after it shifted as the label changed length).
// Sized to fit the longest label, "Investment Entity", without truncating.
const TYPE_FILTER_BUTTON_WIDTH = '12rem';

interface HeaderSearchTypeFilterProps {
  typeLabel: string;
  typeAnchorEl: HTMLElement | null;
  selectedTypes: string[];
  onOpenTypeMenu: (event: React.MouseEvent<HTMLElement>) => void;
  onCloseTypeMenu: () => void;
  onToggleType: (value: string) => void;
  onFocusSearch: () => void;
}

const HeaderSearchTypeFilter: React.FC<HeaderSearchTypeFilterProps> = ({
  typeLabel,
  typeAnchorEl,
  selectedTypes,
  onOpenTypeMenu,
  onCloseTypeMenu,
  onToggleType,
  onFocusSearch,
}) => (
  <InputAdornment position="start" sx={{ ml: 0.5 }}>
    {/* Fixed-width slot so the divider and search input's start position stay
        put regardless of label length - but only THIS is fixed-width, not
        the button itself, so the clickable area doesn't extend into empty
        space past a short label (e.g. "Any"). The leftover space in the slot
        past the button focuses the search input instead, like clicking
        anywhere else on the pill. */}
    <Box
      onClick={(e) => {
        if (e.target === e.currentTarget) onFocusSearch();
      }}
      sx={{ flex: `0 0 ${TYPE_FILTER_BUTTON_WIDTH}`, display: 'flex', cursor: 'text' }}
    >
      <Button
        size="small"
        onClick={onOpenTypeMenu}
        startIcon={<ExpandMoreIcon />}
        disableRipple
        disableElevation
        sx={{
          textTransform: 'none',
          px: 1,
          ml: '1rem',
          backgroundColor: 'transparent',
          borderRadius: 0,
          fontFamily: 'Inter, sans-serif',
          fontSize: '1rem',
          fontWeight: 600,
          color: Palette.default.text,
          lineHeight: 1.2,
          '&:hover': { backgroundColor: 'transparent' },
        }}
      >
        <Box
          component="span"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {typeLabel}
        </Box>
      </Button>
    </Box>
    <Box
      sx={{
        width: 1,
        height: 24,
        mx: 1,
        flexShrink: 0,
        backgroundColor: 'transparent',
      }}
    />
    <Menu anchorEl={typeAnchorEl} open={Boolean(typeAnchorEl)} onClose={onCloseTypeMenu}>
      <MenuItem onClick={() => onToggleType('__ANY__')}>
        <Checkbox size="small" checked={selectedTypes.length === 0} />
        Any
      </MenuItem>
      {SEARCH_TYPE_FILTER_OPTIONS.map((opt) => (
        <MenuItem key={opt} onClick={() => onToggleType(opt)}>
          <Checkbox size="small" checked={selectedTypes.includes(opt)} />
          {SEARCH_TYPE_LABEL_MAP[opt]}
        </MenuItem>
      ))}
    </Menu>
  </InputAdornment>
);

export default HeaderSearchTypeFilter;
